import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoChevronDownOutline, IoCheckmark, IoArrowBack } from "react-icons/io5";
import { submitSupportTicket } from "../../redux/ApiSlice/HelpSlice";

const CATEGORIES = [
    "Account Help",
    "Report a Bug",
    "Request a Feature",
    "Report a Violation",
    "Report an Underage User",
    "Advertising",
    "Marketing",
    "Parental Control Issue",
    "Copyright Issue",
    "Privacy Issue",
];

function CategoryDropdown({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const select = (cat) => {
        onChange(cat);
        setOpen(false);
    };

    return (
        <div ref={ref} className="relative">
            <span className="block text-[12px] font-semibold text-black mb-1">Category</span>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`flex w-full items-center justify-between rounded-[10px] border bg-[#F7FAFC] px-4 py-3 text-[14px] transition-colors ${
                    open ? "border-[#2E9DDA]" : "border-[#96D2F4]"
                } ${value ? "text-[#222222]" : "text-[#9A9A9A]"}`}
            >
                <span>{value || "Select a category"}</span>
                <IoChevronDownOutline
                    className={`shrink-0 text-[#9A9A9A] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <ul className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden rounded-[10px] border border-[#96D2F4] bg-white shadow-lg">
                    {CATEGORIES.map((cat) => (
                        <li key={cat}>
                            <button
                                type="button"
                                onClick={() => select(cat)}
                                className="flex w-full items-center justify-between px-4 py-2.5 text-left text-[14px] text-[#222222] hover:bg-[#EAF7FF] transition-colors"
                            >
                                <span>{cat}</span>
                                {value === cat && <IoCheckmark className="shrink-0 text-[#2E9DDA]" />}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function HelpAndSupport() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading, error } = useSelector((state) => state.help);
    const [form, setForm] = useState({ name: "", email: "", category: "", message: "" });
    const [submitted, setSubmitted] = useState(false);
    const [categoryError, setCategoryError] = useState(false);

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.category) {
            setCategoryError(true);
            return;
        }
        setCategoryError(false);

        try {
            await dispatch(submitSupportTicket({
                name: form.name,
                email: form.email,
                category: form.category,
                message: form.message,
            })).unwrap();
            setSubmitted(true);
            setForm({ name: "", email: "", category: "", message: "" });
        } catch {
            // error is stored in state.help.error by the slice
        }
    };

    return (
        <div className="h-full w-full overflow-y-auto">
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="md:hidden flex items-center gap-1.5 px-4 pt-4 text-[14px] font-semibold text-[#365975]"
                aria-label="Go back"
            >
                <IoArrowBack className="text-[20px]" />
                Back
            </button>

            <div className="mx-auto w-full max-w-[940px] px-10 py-12 lg:py-12">
                <div className="mx-auto max-w-[520px]">
                    <h1 className="text-[30px] font-black text-black">Help & Support</h1>
                    <p className="mt-2 text-[15px] text-[#7A7A7A]">
                        Have a question or need assistance? Fill out the form below and our team will get back to you.
                    </p>

                    {submitted && (
                        <p className="mt-4 text-[14px] font-semibold text-green-600">
                            Thank you! Your request has been submitted. We'll be in touch soon.
                        </p>
                    )}
                    {error && (
                        <p className="mt-4 text-[14px] font-semibold text-red-500">{error}</p>
                    )}

                    <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                        <label className="block rounded-[10px] border border-[#96D2F4] bg-[#F7FAFC] px-4 py-3">
                            <span className="block text-[12px] font-semibold text-black">Name</span>
                            <input
                                type="text"
                                name="name"
                                placeholder="Your name"
                                value={form.name}
                                onChange={handleChange}
                                required
                                className="mt-1 w-full bg-transparent text-[14px] text-[#222222] outline-none placeholder:text-[#9A9A9A]"
                            />
                        </label>

                        <label className="block rounded-[10px] border border-[#96D2F4] bg-[#F7FAFC] px-4 py-3">
                            <span className="block text-[12px] font-semibold text-black">Email Address</span>
                            <input
                                type="email"
                                name="email"
                                placeholder="Enter your email here"
                                value={form.email}
                                onChange={handleChange}
                                required
                                className="mt-1 w-full bg-transparent text-[14px] text-[#222222] outline-none placeholder:text-[#9A9A9A]"
                            />
                        </label>

                        <div>
                            <CategoryDropdown
                                value={form.category}
                                onChange={(cat) => {
                                    setForm((prev) => ({ ...prev, category: cat }));
                                    setCategoryError(false);
                                }}
                            />
                            {categoryError && (
                                <p className="mt-1 text-[12px] text-red-500">Please select a category.</p>
                            )}
                        </div>

                        <label className="block rounded-[10px] border border-[#96D2F4] bg-[#F7FAFC] px-4 py-3">
                            <span className="block text-[12px] font-semibold text-black">Message</span>
                            <textarea
                                rows={5}
                                name="message"
                                placeholder="Describe your issue or question..."
                                maxLength={500}
                                value={form.message}
                                onChange={handleChange}
                                required
                                className="mt-1 w-full resize-none bg-transparent text-[14px] text-[#222222] outline-none placeholder:text-[#9A9A9A]"
                            />
                            <span className={`block text-right text-[11px] mt-1 ${form.message.length >= 500 ? "text-red-500" : "text-[#9A9A9A]"}`}>
                                {form.message.length}/500
                            </span>
                        </label>

                        <button
                            type="submit"
                            disabled={loading}
                            className="h-9 rounded-full bg-[#BEE6FF] px-8 text-[14px] font-semibold text-[#1B2E3B] hover:bg-[#A7DBFB] disabled:opacity-60"
                        >
                            {loading ? "Submitting..." : "Submit"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
